import React, { PureComponent } from 'react';
import { Flex } from '../../common/Flex';
import {
    EnrichedFlightId,
    FlightDto,
    FlightId,
    FlightLevel_Unit,
    FlightLevelBand,
    FlightModelType,
    MapFlightsDataDto,
    MapTrafficVolume,
    TimePeriod,
    VpdFlightDataDto,
} from 'app/model/CORE';
import Utils from '../../../common/util/Utils';
import { TimeSelector } from '../../map/control/TimeSelector';
import TimeUtils from '../../../common/util/TimeUtils';
import { FlightLevelSelector } from '../../map/control/FlightLevelSelector';
import { CloseSquareOutlined, PauseCircleOutlined, PlayCircleOutlined, SettingFilled } from '@ant-design/icons';
import { IMapProps } from '../../map/CoreMap';
import VerticalProfileSvg from '../../map/vpd/VerticalProfileSvg';
import { FlightMapOptions, ProfileSegmentHighlight } from '../../map/model/MapModel';
import FlightUtils from '../../../common/util/FlightUtils';
import { ExtendedMonitoredTv, NetworkMapConvection } from 'app/model/SOL49';
import memoizeOne from 'memoize-one';
import { ClosablePopover } from 'app/components/common/ClosablePopover';
import { MapSettings, MapSettingsContent, MapSettingsTitle } from 'app/components/dashboard/network/map/MapSettings';
import { ConvectionLabelContent, ConvectionLabelTitle } from 'app/components/dashboard/network/map/ConvectionLabel';
import { MeasureMapTrafficVolume } from 'app/components/dashboard/network/map/Map';
import MapUtils from 'app/components/map/util/MapUtils';
import ConditionalRender from 'app/common/components/ConditionalRender';
import { RangeSelectorIcon } from 'app/components/icons/RangeSelectorIcon';
import { LabelDisplayMode, TimePeriodSelector } from 'app/components/map/control/TimePeriodSelector';
import AppContext from 'app/common/context/AppContext';
import { DwdTPShortcut } from 'app/components/dashboard/network/map/DwdTPShortcut';
import { UnityApplication } from 'app/unity/UnityApplication';
import { NMUI4DContext, NMUI4DContextProps } from 'app/common/context/NMUI4DContext';

import { ControlDisplayOpts } from '../../map/MapDisplay';

export const DEFAULT_FL_FILTER_RANGE = {
    min: { value: 0, unit: FlightLevel_Unit.FLIGHT_LEVEL },
    max: { value: 999, unit: FlightLevel_Unit.FLIGHT_LEVEL },
};

export const DEFAULT_FLIGHT_MAP_OPTIONS: FlightMapOptions = {
    autoZoom: false,
    centerOnSelectedTvs: true,
    listVectorPoints: true,
    showActiveAirspaceOnly: true,
    showActiveDynamicVolumeInVpd: false,
    showActiveDynamicVolumeOnly: false,
    showElementaryAirspaceOnly: true,
    showIfpzPartOfTrajectoryOnly: false,
};

export interface AbstractMapViewState {
    //stateless
}

export interface AbstractMapViewProps {
    currentTime: Date;
    timePeriod: TimePeriod;
    selectedTvs: Array<ExtendedMonitoredTv>;
    selectedFlights: Array<FlightDto>;
    monitoredTvs: Array<ExtendedMonitoredTv>;
    // tvsOccupancyPeriods?: { [key: string]: Map<FlightId, TimePeriod> }
    visible: boolean;
    convectionData: NetworkMapConvection;
    // airspaceDef: { [key: string]: CORE.Airspace }//deprecated?
    mapSettings: MapSettings;

    controlDisplayOpts?: ControlDisplayOpts;

    onMapSettingsChanged(mapSettings: MapSettings): void;

    //moved from state
    flightData: MapFlightsDataDto;
    selectedMapTVs: Array<MapTrafficVolume>;
    selectedMapMeasuresTVs: Array<MeasureMapTrafficVolume>;
    highlightedSegments: Map<FlightId, ProfileSegmentHighlight[]>;
    flightLevelFilter: FlightLevelBand;

    onFlightLevelFilterChange(value: FlightLevelBand): void;

    //if not provided are true by default
    showTimePeriodSelection?: boolean;
    showTimeReset: boolean;
    showTimePeriodReset: boolean;
    showMapSettings?: boolean;
    flightLevelFilterLocation?: 'header' | 'left'; //if undefined the default location will be the header

    mapRefTime: Date;

    onMapRefTimeChange(date: Date): void;

    mapRefTimePeriod: TimePeriod;
    timePeriodSelection: boolean;

    onTimePeriodSelectionChange(value: boolean): void;

    onMapRefTimePeriodChange(value: TimePeriod): void;

    onResetMapRefTime(): void;

    onResetMapRefTimePeriod(): void;

    headerExtraContent?: React.ReactNode;
    headerSize?: string;

    timeAnimation?: boolean;
    onTimeAnimationChange?: (value: boolean) => void;
    showDwdTPShortcuts?: boolean;
}

export class AbstractMapView<
    Props extends AbstractMapViewProps = AbstractMapViewProps,
    State extends AbstractMapViewState = AbstractMapViewState,
> extends PureComponent<Props, State> {
    private settingsPopoverRef = React.createRef<ClosablePopover>();
    protected convectionLegendPopoverRef = React.createRef<ClosablePopover>();

    public static contextType = NMUI4DContext;
    protected nmui4dContext?: NMUI4DContextProps;

    componentDidMount(): void {
        this.nmui4dContext = this.context as NMUI4DContextProps;

        // Handle the case where initial props contain elements to be displayed
        this.componentDidUpdate(undefined, undefined);
    }

    componentDidUpdate(prevProps: AbstractMapViewProps, prevState): void {
        if (this.props.visible) {
            if (
                this.props.mapSettings.showConvection &&
                this.props.mapSettings.showConvectionLegend &&
                (prevProps?.mapSettings?.showConvectionLegend !== this.props.mapSettings.showConvectionLegend ||
                    prevProps?.mapSettings.showConvection !== this.props.mapSettings.showConvection)
            ) {
                this.convectionLegendPopoverRef.current?.show();
            }

            if (
                (!this.props.mapSettings.showConvection || !this.props.mapSettings.showConvectionLegend) &&
                (prevProps?.mapSettings?.showConvectionLegend !== this.props.mapSettings.showConvectionLegend ||
                    prevProps?.mapSettings.showConvection !== this.props.mapSettings.showConvection)
            ) {
                this.convectionLegendPopoverRef.current?.hide();
            }

            //when show the map again, show the convection legend if needed
            if (
                this.props.mapSettings.showConvection &&
                this.props.mapSettings.showConvectionLegend &&
                !this.convectionLegendPopoverRef.current?.isVisible()
            ) {
                this.convectionLegendPopoverRef.current?.show();
            }
        } else {
            //when hide the map, hide the convection legend
            if (prevProps?.visible && this.props.mapSettings.showConvectionLegend) {
                this.convectionLegendPopoverRef?.current?.hide();
            }
        }
    }

    protected getVpdCrossings = (flightId: EnrichedFlightId): VpdFlightDataDto => {
        if (this.props.flightData?.vpdFlightDataList)
            return this.props.flightData.vpdFlightDataList.find(el =>
                FlightUtils.isSameEnrichedFlight(flightId, el.flightId),
            );
        return null;
    };

    getHighestModelType = (flight: FlightDto): FlightModelType => {
        return (
            (Utils.isNotEmpty(flight.ctfmPoints) && FlightModelType.CTFM) ||
            (Utils.isNotEmpty(flight.rtfmPoints) && FlightModelType.RTFM) ||
            (Utils.isNotEmpty(flight.ftfmPoints) && FlightModelType.FTFM)
        );
    };

    /*
      Returns true if there is only one flight selected, with at least one point/airspace profile available
     */
    showVpd = () => {
        if (this.props?.flightData?.flights?.length !== 1) return false;

        const flight: FlightDto = this.props.flightData.flights[0];
        const modelType = this.getHighestModelType(flight);

        return Utils.isExists(flight) && Utils.isExists(modelType);
    };

    renderVpd = (highlightedSegments: ProfileSegmentHighlight[], flightMapOptions: FlightMapOptions) => {
        if (!this.showVpd()) return undefined;

        const flight: FlightDto = this.props.flightData.flights[0];
        const modelType = this.getHighestModelType(flight);

        //get the crossings only if there is a selected flight
        const vpdCrossings: VpdFlightDataDto = flight && this.getVpdCrossings(FlightUtils.getEnrichedFlightId(flight));

        return (
            <VerticalProfileSvg
                flight={flight}
                modelType={modelType}
                vpdCrossings={vpdCrossings}
                showIfpuzOnly={flightMapOptions.showIfpzPartOfTrajectoryOnly}
                highlightedPoints={null}
                highlightedSegments={highlightedSegments}
                pointSelectionHandler={() => {}}
                showDynamicVolumeCrossings={flightMapOptions.showActiveDynamicVolumeInVpd}
                airspacesOfInterest={[]}
            />
        );
    };

    renderMapSettings = () => {
        return (
            <span className="detailed-subpanel-header-right">
                <ClosablePopover
                    key="map-settings-action"
                    ref={this.settingsPopoverRef}
                    trigger="click"
                    placement="bottom"
                    title={<MapSettingsTitle />}
                    content={
                        <MapSettingsContent
                            settings={this.props.mapSettings}
                            onChange={this.handleSettingsChange}
                            onClose={() => this.settingsPopoverRef.current.hide()}
                        />
                    }>
                    <SettingFilled
                        onClick={() => this.settingsPopoverRef.current.show()}
                        style={{ fontSize: '1.5em', paddingLeft: '0.5em' }}
                    />
                </ClosablePopover>
            </span>
        );
    };

    /**
     * renders map Convection legend popup
     */
    renderConvectionLegend = () => {
        return (
            <span style={{ width: '100%' }} className="ol-full-screen">
                <ClosablePopover
                    key="convection-label"
                    ref={this.convectionLegendPopoverRef}
                    trigger="click"
                    placement="bottomLeft"
                    autoHide={false}
                    title={<ConvectionLabelTitle onClose={this.closeConvectionLegend} />}
                    content={<ConvectionLabelContent source={AppContext.getConvectionSource()} />}></ClosablePopover>
            </span>
        );
    };

    private closeConvectionLegend = () => {
        this.convectionLegendPopoverRef.current?.hide();
        this.props.onMapSettingsChanged({ ...this.props.mapSettings, showConvectionLegend: false });
    };

    protected vpdHighlightedSegments = memoizeOne(
        (
            highlightedSegments: Map<FlightId, ProfileSegmentHighlight[]>,
            flightId: FlightId,
        ): ProfileSegmentHighlight[] => {
            if (!highlightedSegments) return;

            const key = Array.from(highlightedSegments.keys()).find(key => FlightUtils.isSameFlight(key, flightId));
            return highlightedSegments.get(key);
        },
    );

    renderHeader() {
        return (
            <Flex
                size={Utils.isExists(this.props.headerSize) ? this.props.headerSize : '2vw'}
                style={{ alignItems: 'center' }}
                className={'map-header'}>
                {this.renderHeaderTimeSlider(false)}
                <ConditionalRender
                    condition={
                        this.props.flightLevelFilterLocation === 'header' ||
                        Utils.isNotExists(this.props.flightLevelFilterLocation)
                    }>
                    <FlightLevelSelector
                        label="FLIGHT LEVEL"
                        length="20em"
                        markIntervalFL={20}
                        flRange={this.props.flightLevelFilter}
                        onFlRangeChange={this.handleFlRangeChange}
                    />
                </ConditionalRender>

                <ConditionalRender condition={Utils.isExists(this.props.headerExtraContent)}>
                    {this.props.headerExtraContent}
                </ConditionalRender>

                <ConditionalRender
                    condition={this.props.showMapSettings || Utils.isNotExists(this.props.showMapSettings)}>
                    <Flex grow={false} shrink={false} style={{ justifyContent: 'flex-end' }}>
                        {this.renderMapSettings()}
                    </Flex>
                </ConditionalRender>
            </Flex>
        );
    }

    renderHeaderTimeSlider(showLabel: boolean) {
        const showTimePeriodSelection =
            Utils.isNotExists(this.props.showTimePeriodSelection) || this.props.showTimePeriodSelection;
        const currentTimeWithoutSeconds = TimeUtils.withoutSeconds(this.props.currentTime);
        const currentTimeEqualsAnyTimePeriodRange: boolean =
            showTimePeriodSelection &&
            (Utils.isEquals(currentTimeWithoutSeconds, TimeUtils.withoutSeconds(this.props.mapRefTimePeriod?.from)) ||
                Utils.isEquals(currentTimeWithoutSeconds, TimeUtils.withoutSeconds(this.props.mapRefTimePeriod?.to)));
        return (
            <Flex className="detailed-subpanel-header-left" style={{ gap: '1em' }}>
                <ConditionalRender condition={showLabel}>
                    <span className="detailed-subpanel-title">TIME</span>
                </ConditionalRender>
                <Flex direction={'vertical'} style={{ gap: '1em' }}>
                    <Flex style={{ gap: '0.5em', alignItems: 'center' }}>
                        <ConditionalRender condition={showTimePeriodSelection}>
                            <RangeSelectorIcon
                                onClick={this.handleTimePeriodSelection}
                                style={{ marginTop: '0.5em' }}
                                title="Switch time mode"
                                className={this.props.timePeriodSelection ? 'iconSelected icon' : 'icon'}
                            />
                        </ConditionalRender>

                        <ConditionalRender condition={!this.props.timePeriodSelection}>
                            <TimeSelector
                                width="20em"
                                timePeriod={this.props.timePeriod}
                                value={this.props.mapRefTime}
                                onChange={this.handleMapRefTimeChange}
                                currentTickColor={this.props.showTimeReset ? '#60B1CC' : undefined}
                                markIntervalMs={20 * TimeUtils.MIN_TO_MS}
                            />
                        </ConditionalRender>
                        <ConditionalRender condition={showTimePeriodSelection && this.props.timePeriodSelection}>
                            <TimePeriodSelector
                                width="20em"
                                value={this.props.mapRefTimePeriod}
                                labelDisplayMode={LabelDisplayMode.ABSOLUTE}
                                currentTime={currentTimeWithoutSeconds}
                                markIntervalMs={10 * TimeUtils.MIN_TO_MS}
                                showCurrentTime={!currentTimeEqualsAnyTimePeriodRange}
                                currentTimeLineColor="#E6E6E6"
                                timePeriodRange={this.props.timePeriod}
                                hideDownLabels={true}
                                onChange={this.handleMapRefTimePeriodChange}
                            />
                        </ConditionalRender>
                        <div>
                            <ConditionalRender
                                condition={
                                    !this.props.timeAnimation &&
                                    (this.props.timePeriodSelection
                                        ? this.props.showTimePeriodReset
                                        : this.props.showTimeReset)
                                }>
                                <CloseSquareOutlined
                                    className="icon"
                                    title="Reset time"
                                    style={{ fontSize: '2em' }}
                                    onClick={this.handleResetTimeClick}
                                />
                            </ConditionalRender>
                        </div>
                        <div>
                            <ConditionalRender condition={!this.props.timeAnimation}>
                                <PlayCircleOutlined
                                    className="icon"
                                    title="Play timelapse"
                                    onClick={() => this.props.onTimeAnimationChange(true)}
                                    style={{ fontSize: '2em' }}
                                />
                            </ConditionalRender>
                            <ConditionalRender condition={this.props.timeAnimation}>
                                <PauseCircleOutlined
                                    className="icon"
                                    title="Pause timelapse"
                                    onClick={() => this.props.onTimeAnimationChange(false)}
                                    style={{ fontSize: '2em' }}
                                />
                            </ConditionalRender>
                        </div>
                    </Flex>
                    <ConditionalRender condition={this.props.showDwdTPShortcuts}>
                        <DwdTPShortcut
                            value={this.props.timePeriodSelection ? this.props.mapRefTimePeriod : this.props.mapRefTime}
                            onChange={tp => {
                                this.props.timePeriodSelection
                                    ? this.handleMapRefTimePeriodChange(tp)
                                    : this.props.onTimePeriodSelectionChange(true);
                                this.handleMapRefTimePeriodChange(tp);
                            }}
                        />
                    </ConditionalRender>
                </Flex>
                {/*</Fragment>*/}
            </Flex>
        );
    }

    renderMap() {
        const customControls = <div style={{ zIndex: 1, margin: '5px' }}>{this.renderConvectionLegend()}</div>;

        // NOTE(bjarne): In mapless mode the UnityApplication is already created in App.tsx.
        return this.nmui4dContext?.isMapless ? (
            customControls
        ) : (
            <UnityApplication customControls={customControls}></UnityApplication>
        );
    }

    getExtraCoreMapProperties(): Partial<IMapProps> {
        return null;
    }

    renderContent() {
        let vpdHighlightedSegments: ProfileSegmentHighlight[];
        if (this.showVpd()) {
            const flight: FlightDto = this.props.flightData.flights[0];
            vpdHighlightedSegments = this.vpdHighlightedSegments(this.props.highlightedSegments, flight.flightId);
        }

        return (
            <>
                <Flex className="layout-content-map-id">{this.renderMap()}</Flex>
                <ConditionalRender condition={this.showVpd()}>
                    <Flex
                        size={this.showVpd() ? '25%' : '0%'}
                        style={{ backgroundColor: '#191919', paddingTop: '1em' }}>
                        {this.renderVpd(vpdHighlightedSegments, DEFAULT_FLIGHT_MAP_OPTIONS)}
                    </Flex>
                </ConditionalRender>
            </>
        );
    }

    renderLeftContent() {
        return (
            <ConditionalRender condition={this.props.flightLevelFilterLocation === 'left'}>
                {/*<Flex direction={"horizontal"}>*/}
                <Flex size="2.5em" direction="vertical">
                    <Flex size="4vh" />
                    <Flex>
                        <FlightLevelSelector
                            label=""
                            length="100%"
                            markIntervalFL={20}
                            orientation="vertical"
                            flRange={this.props.flightLevelFilter}
                            onFlRangeChange={this.handleFlRangeChange}
                        />
                    </Flex>
                    <Flex size="4vh" />
                </Flex>
                {/*</Flex>*/}
            </ConditionalRender>
        );
    }

    renderRightContent() {
        return null;
    }

    render() {
        if (!this.props.visible || !this.props.mapRefTime || !this.props.mapRefTimePeriod) return null;

        return (
            <Flex direction="horizontal">
                {this.renderLeftContent()}
                <Flex
                    direction="vertical"
                    style={{ paddingLeft: '1em', paddingBottom: '1em', height: '100%', gap: '1em' }}>
                    {this.renderHeader()}
                    {this.renderContent()}
                </Flex>
                {this.renderRightContent()}
            </Flex>
        );
    }

    // HANDLERS
    handleSettingsChange = (value: MapSettings) => {
        this.props.onMapSettingsChanged(value);
        this.settingsPopoverRef.current.hide();
    };

    handleMapRefTimeChange = (refTime: Date) => this.props.onMapRefTimeChange(refTime);

    handleFlRangeChange = (flRange: FlightLevelBand) => this.props.onFlightLevelFilterChange(flRange);

    handleTimePeriodSelection = () => {
        this.props.onTimePeriodSelectionChange(!this.props.timePeriodSelection);
    };

    handleMapRefTimePeriodChange = (value: TimePeriod) => {
        this.props.onMapRefTimePeriodChange(value);
    };

    handleResetTimeClick = () => {
        if (this.props.timePeriodSelection) {
            this.props.onResetMapRefTimePeriod();
        } else {
            this.props.onResetMapRefTime();
        }
    };
}
